import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle,
  Database, Activity, ArrowLeft, Stethoscope, Wifi, Server, Globe
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, goldRateAPI } from '../../services/api';

const AdminGoldRateAuditPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [runningDiagnostic, setRunningDiagnostic] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [liveRate, setLiveRate] = useState(null);
  const [diagnosticResult, setDiagnosticResult] = useState(null);

  const fetchAuditData = async () => {
    try {
      const [auditResponse, rateResponse] = await Promise.all([
        adminAPI.getGoldRateAudit(),
        goldRateAPI.getRate()
      ]);
      setAuditData(auditResponse.data);
      setLiveRate(rateResponse.data);
    } catch (error) {
      toast.error('Failed to fetch audit data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditData();
  }, []);

  const handleRefreshRate = async () => {
    setRefreshing(true);
    try {
      const response = await adminAPI.refreshGoldRate();
      toast.success(`Rate refreshed: ₹${response.data.rate?.rate_22kt}/gram`);
      await fetchAuditData();
    } catch (error) {
      toast.error('Failed to refresh rate');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRunDiagnostic = async () => {
    setRunningDiagnostic(true);
    setDiagnosticResult(null);
    try {
      const response = await adminAPI.getScrapeDiagnostic();
      setDiagnosticResult(response.data);
      toast.success('Diagnostic completed');
    } catch (error) {
      toast.error('Failed to run diagnostic');
      setDiagnosticResult({ error: error.response?.data?.detail || error.message });
    } finally {
      setRunningDiagnostic(false);
    }
  };

  const getDiagnosticStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'timeout':
        return <Clock className="w-4 h-4 text-orange-600" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Success</Badge>;
      case 'fallback_success':
        return <Badge className="bg-yellow-100 text-yellow-800"><AlertTriangle className="w-3 h-3 mr-1" /> Fallback</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" /> Failed</Badge>;
      case 'cache_hit':
        return <Badge className="bg-blue-100 text-blue-800"><Database className="w-3 h-3 mr-1" /> Cache Hit</Badge>;
      case 'manual_refresh_triggered':
        return <Badge className="bg-purple-100 text-purple-800"><RefreshCw className="w-3 h-3 mr-1" /> Manual</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', { 
      dateStyle: 'short', 
      timeStyle: 'medium',
      hour12: true 
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin')}
            className="text-gray-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-navy">Gold Rate Audit</h1>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleRunDiagnostic}
            disabled={runningDiagnostic}
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <Stethoscope className={`w-4 h-4 mr-2 ${runningDiagnostic ? 'animate-pulse' : ''}`} />
            {runningDiagnostic ? 'Running...' : 'Run Diagnostic'}
          </Button>
          <Button
            onClick={handleRefreshRate}
            disabled={refreshing}
            className="bg-gold hover:bg-gold/90 text-navy"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh Rate Now
          </Button>
        </div>
      </div>

      {/* Diagnostic Results */}
      {diagnosticResult && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Stethoscope className="w-5 h-5" />
              Network Diagnostic Results
              <span className="text-xs font-normal text-blue-600 ml-auto">
                {diagnosticResult.timestamp && new Date(diagnosticResult.timestamp).toLocaleString('en-IN')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {diagnosticResult.error ? (
              <p className="text-red-600">Error: {diagnosticResult.error}</p>
            ) : (
              <div className="space-y-4">
                {/* Test Results */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* DNS Resolution */}
                  {diagnosticResult.tests?.dns_resolution && (
                    <div className={`p-3 rounded-lg border ${diagnosticResult.tests.dns_resolution.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Globe className="w-4 h-4" />
                        <span className="font-medium text-sm">DNS Resolution</span>
                        {getDiagnosticStatusIcon(diagnosticResult.tests.dns_resolution.status)}
                      </div>
                      {diagnosticResult.tests.dns_resolution.ip && (
                        <p className="text-xs text-gray-600">IP: {diagnosticResult.tests.dns_resolution.ip}</p>
                      )}
                      {diagnosticResult.tests.dns_resolution.time_ms && (
                        <p className="text-xs text-gray-600">{diagnosticResult.tests.dns_resolution.time_ms}ms</p>
                      )}
                      {diagnosticResult.tests.dns_resolution.error && (
                        <p className="text-xs text-red-600">{diagnosticResult.tests.dns_resolution.error}</p>
                      )}
                    </div>
                  )}

                  {/* TCP Connection */}
                  {diagnosticResult.tests?.tcp_connection && (
                    <div className={`p-3 rounded-lg border ${diagnosticResult.tests.tcp_connection.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Wifi className="w-4 h-4" />
                        <span className="font-medium text-sm">TCP Connection (443)</span>
                        {getDiagnosticStatusIcon(diagnosticResult.tests.tcp_connection.status)}
                      </div>
                      {diagnosticResult.tests.tcp_connection.time_ms && (
                        <p className="text-xs text-gray-600">{diagnosticResult.tests.tcp_connection.time_ms}ms</p>
                      )}
                      {diagnosticResult.tests.tcp_connection.error && (
                        <p className="text-xs text-red-600">{diagnosticResult.tests.tcp_connection.error}</p>
                      )}
                    </div>
                  )}

                  {/* HTTPS Requests */}
                  {Object.entries(diagnosticResult.tests || {})
                    .filter(([key]) => key.startsWith('https_request'))
                    .map(([key, test]) => (
                      <div key={key} className={`p-3 rounded-lg border ${test.status === 'success' ? 'bg-green-50 border-green-200' : test.status === 'timeout' ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Server className="w-4 h-4" />
                          <span className="font-medium text-sm">HTTPS ({key.replace('https_request_', '')})</span>
                          {getDiagnosticStatusIcon(test.status)}
                        </div>
                        {test.status_code && (
                          <p className="text-xs text-gray-600">Status: {test.status_code}</p>
                        )}
                        {test.time_ms && (
                          <p className="text-xs text-gray-600">{test.time_ms}ms</p>
                        )}
                        {test.rate_found && (
                          <p className="text-xs text-green-700 font-medium">Rate found: ₹{test.rate_found}</p>
                        )}
                        {test.content_length && (
                          <p className="text-xs text-gray-500">{(test.content_length / 1024).toFixed(1)}KB</p>
                        )}
                        {test.error && (
                          <p className="text-xs text-red-600 truncate" title={test.error}>{test.error}</p>
                        )}
                      </div>
                    ))}
                </div>

                {/* Cache Status from diagnostic */}
                {diagnosticResult.cache_status && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="font-medium text-sm text-gray-700 mb-2">Cache Status at Diagnostic Time:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">Valid:</span>{' '}
                        <span className={diagnosticResult.cache_status.cache_valid ? 'text-green-600' : 'text-red-600'}>
                          {diagnosticResult.cache_status.cache_valid ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Rate:</span>{' '}
                        <span className="text-gray-700">₹{diagnosticResult.cache_status.cached_rate || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Age:</span>{' '}
                        <span className="text-gray-700">{diagnosticResult.cache_status.cache_age_minutes?.toFixed(1) || '0'} min</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Source:</span>{' '}
                        <span className="text-gray-700">{diagnosticResult.cache_status.source || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Live Rate</p>
                <p className="text-2xl font-bold text-navy">
                  ₹{liveRate?.rate_22kt?.toLocaleString() || 'N/A'}
                </p>
                <p className="text-xs text-gray-400">per gram (22KT) • {liveRate?.source || 'N/A'}</p>
              </div>
              <Activity className="w-8 h-8 text-gold" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cache Status</p>
                <p className={`text-lg font-bold ${auditData?.cache_info?.cache_valid ? 'text-green-600' : 'text-orange-500'}`}>
                  {auditData?.cache_info?.cache_valid ? 'Valid' : 'Stale'}
                </p>
                <p className="text-xs text-gray-400">
                  Age: {auditData?.cache_info?.cache_age_minutes?.toFixed(1) || '0'} min
                </p>
              </div>
              <Database className={`w-8 h-8 ${auditData?.cache_info?.cache_valid ? 'text-green-500' : 'text-orange-500'}`} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">24h Success</p>
                <p className="text-2xl font-bold text-green-600">
                  {auditData?.statistics?.last_24h_success || 0}
                </p>
                <p className="text-xs text-gray-400">scrapes</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">24h Failures</p>
                <p className={`text-2xl font-bold ${auditData?.statistics?.last_24h_failures > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  {auditData?.statistics?.last_24h_failures || 0}
                </p>
                <p className="text-xs text-gray-400">scrapes</p>
              </div>
              <XCircle className={`w-8 h-8 ${auditData?.statistics?.last_24h_failures > 0 ? 'text-red-500' : 'text-gray-400'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Scraping Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Recent Scraping Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {auditData?.logs?.length > 0 ? (
            <div className="space-y-3">
              {auditData.logs.map((log, index) => (
                <div 
                  key={log.log_id || index}
                  className={`p-4 rounded-lg border ${
                    log.status === 'failed' ? 'border-red-200 bg-red-50' :
                    log.status === 'success' ? 'border-green-200 bg-green-50' :
                    log.status === 'fallback_success' ? 'border-yellow-200 bg-yellow-50' :
                    'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(log.status)}
                        <span className="text-sm text-gray-500">
                          Source: <span className="font-medium">{log.source}</span>
                        </span>
                        {log.response_time_ms && (
                          <span className="text-xs text-gray-400">
                            ({log.response_time_ms}ms)
                          </span>
                        )}
                      </div>
                      {log.rate_22kt && (
                        <p className="text-lg font-semibold text-navy">
                          ₹{log.rate_22kt.toLocaleString()}/gram
                        </p>
                      )}
                      {log.error_message && (
                        <p className="text-sm text-red-600 mt-1">
                          Error: {log.error_message}
                        </p>
                      )}
                      {log.triggered_by && (
                        <p className="text-sm text-purple-600 mt-1">
                          Triggered by: {log.triggered_by}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">
                        {formatDateTime(log.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No scraping logs yet</p>
              <p className="text-sm">Logs will appear after the first rate fetch</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cache Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Cache Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Cache Duration</p>
              <p className="font-semibold">{auditData?.cache_info?.cache_duration_minutes || 15} minutes</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Last Updated</p>
              <p className="font-semibold">{formatDateTime(auditData?.cache_info?.cache_updated_at)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Logs</p>
              <p className="font-semibold">{auditData?.statistics?.total_logs || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">24h Cache Hits</p>
              <p className="font-semibold">{auditData?.statistics?.last_24h_cache_hits || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminGoldRateAuditPage;
